import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { guestVisitApi, GuestInfo } from 'shared/services/guestVisitApi';
import { queryKeys } from '../queryConfig';

export interface GuestVisitsFilters {
  company?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export const useGuestVisits = (filters: GuestVisitsFilters = {}) => {
  const queryParams = {
    page: filters.page || 1,
    limit: filters.limit || 20,
    ...(filters.company && { company: filters.company }),
    ...(filters.status && { status: filters.status }),
  };

  return useQuery({
    queryKey: queryKeys.guestVisits.list(queryParams, filters.company || ''),
    queryFn: () => guestVisitApi.getAll(queryParams),
    placeholderData: keepPreviousData,
  });
};

export const useCreateGuestVisit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { guests: GuestInfo[]; startDate: string; endDate: string; notes?: string; companyId?: string; accessStartTime?: string; accessEndTime?: string }) =>
      guestVisitApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.guestVisits.all });
    },
  });
};

export const useUpdateGuestVisit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { guests: GuestInfo[]; startDate: string; endDate: string; notes?: string; accessStartTime?: string; accessEndTime?: string };
    }) => guestVisitApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.guestVisits.all });
    },
  });
};

export const useDeleteGuestVisit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => guestVisitApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.guestVisits.all });
    },
  });
};

export const useCheckInGuest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, guestIndex }: { id: string; guestIndex: number }) =>
      guestVisitApi.checkIn(id, guestIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.guestVisits.all });
    },
  });
};

export const useCheckOutGuest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, guestIndex }: { id: string; guestIndex: number }) =>
      guestVisitApi.checkOut(id, guestIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.guestVisits.all });
    },
  });
};

export const useCheckInAllGuests = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => guestVisitApi.checkInAll(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.guestVisits.all });
    },
  });
};

export const useCheckOutAllGuests = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => guestVisitApi.checkOutAll(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.guestVisits.all });
    },
  });
};

export const useVerifyGuestVisit = (code: string) => {
  return useQuery({
    queryKey: queryKeys.guestVisits.detail(code),
    queryFn: () => guestVisitApi.verifyByCode(code),
    enabled: !!code,
  });
};
